import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Plus, Pencil, Trash2, Loader2, Video, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Printer, ScanLine } from "lucide-react";
import ScanSheetDialog from "@/components/ScanSheetDialog";
import { api, apiError } from "@/lib/api";
import TeamCrest from "@/components/TeamCrest";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import BroadcastPanel from "@/components/BroadcastPanel";

const toLocalInput = (iso) => {
    const d = new Date(iso);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
};

const defaultDate = () => {
    const d = new Date(Date.now() + 3 * 86400000);
    d.setHours(19, 0, 0, 0);
    return toLocalInput(d.toISOString());
};

const VENUES = ["St. Pats", "Sir John", "Weledeh", "Multiplex", "Range Lake", "St. Joe", "William Mac"];
const EMPTY = { home_team_id: "", away_team_id: "", date: "", venue: "", week: "", status: "scheduled", home_score: "", away_score: "", stream_url: "", quarter: "Q1", clock: "", player_of_the_game_id: "", potg_statline: "", featured_home_player_id: "", featured_away_player_id: "" };

const QUARTERS = ["Q1", "Q2", "HT", "Q3", "Q4", "OT"];

function LiveBoard({ game, onSaved }) {
    const [busy, setBusy] = useState(false);
    const [quarter, setQuarter] = useState(game.quarter || "Q1");
    const [clock, setClock] = useState(game.clock || "");

    const push = async (patch = {}) => {
        setBusy(true);
        try {
            await api.put(`/games/${game.id}`, {
                home_team_id: game.home_team_id,
                away_team_id: game.away_team_id,
                date: game.date,
                venue: game.venue || "",
                week: game.week ?? null,
                status: patch.status || "live",
                home_score: patch.home_score ?? game.home_score ?? 0,
                away_score: patch.away_score ?? game.away_score ?? 0,
                stream_url: game.stream_url || "",
                quarter: patch.quarter ?? quarter,
                clock: patch.clock ?? clock,
                player_of_the_game_id: game.player_of_the_game_id || null,
                potg_statline: game.potg_statline || "",
            });
            onSaved();
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    const sides = [
        { key: "home", team: game.home_team, score: game.home_score ?? 0 },
        { key: "away", team: game.away_team, score: game.away_score ?? 0 },
    ];

    return (
        <div data-testid={`live-board-${game.id}`} className="w-full mt-4 pt-4 border-t border-[#262638]">
            <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#EF4444]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] animate-pulse" /> Live scoring control
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                {sides.map((s) => (
                    <div key={s.key} className="bg-[#08080C] rounded-xl p-4 border border-[#262638]">
                        <div className="flex items-center justify-between">
                            <span className="font-display font-bold uppercase text-sm text-white">
                                {s.team?.short_name}
                            </span>
                            <span className="font-stat font-bold text-3xl text-[#EF4444]">{s.score}</span>
                        </div>
                        <div className="flex gap-2 mt-3">
                            {[1, 2, 3].map((pts) => (
                                <button
                                    key={pts}
                                    data-testid={`live-${s.key}-plus${pts}-${game.id}`}
                                    disabled={busy}
                                    onClick={() =>
                                        push(s.key === "home" ? { home_score: s.score + pts } : { away_score: s.score + pts })
                                    }
                                    className="flex-1 bg-[#FF5500] hover:bg-[#E04800] disabled:opacity-50 text-white font-stat font-bold text-sm py-2 rounded-lg transition-colors duration-200"
                                >
                                    +{pts}
                                </button>
                            ))}
                            <button
                                data-testid={`live-${s.key}-minus1-${game.id}`}
                                disabled={busy || s.score === 0}
                                onClick={() =>
                                    push(s.key === "home" ? { home_score: s.score - 1 } : { away_score: s.score - 1 })
                                }
                                className="px-3 border border-[#262638] hover:border-[#EF4444] hover:text-[#EF4444] disabled:opacity-50 text-slate-300 font-stat font-bold text-sm py-2 rounded-lg transition-colors duration-200"
                            >
                                −1
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex flex-wrap items-end gap-3 mt-4">
                <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Quarter</Label>
                    <Select value={quarter} onValueChange={setQuarter}>
                        <SelectTrigger data-testid={`live-quarter-${game.id}`} className="mt-2 w-28 bg-[#08080C] border-[#262638] text-white">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#12121A] border-[#262638] text-white">
                            {QUARTERS.map((q) => (
                                <SelectItem key={q} value={q}>
                                    {q}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Clock</Label>
                    <Input
                        data-testid={`live-clock-input-${game.id}`}
                        value={clock}
                        onChange={(e) => setClock(e.target.value)}
                        placeholder="4:32"
                        className="mt-2 w-28 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white font-stat"
                    />
                </div>
                <button
                    data-testid={`live-clock-save-${game.id}`}
                    disabled={busy}
                    onClick={() => push({})}
                    className="border border-[#262638] hover:border-[#FFB800] hover:text-[#FFB800] disabled:opacity-50 text-slate-300 font-display font-bold uppercase tracking-widest text-xs px-4 py-2.5 rounded-full transition-colors duration-200"
                >
                    Update Clock
                </button>
                <button
                    data-testid={`live-end-game-${game.id}`}
                    disabled={busy}
                    onClick={() => {
                        if (window.confirm("End the game and mark it Final?")) push({ status: "final" });
                    }}
                    className="bg-[#22C55E]/15 border border-[#22C55E]/50 hover:bg-[#22C55E] hover:text-white disabled:opacity-50 text-[#22C55E] font-display font-bold uppercase tracking-widest text-xs px-4 py-2.5 rounded-full transition-colors duration-200"
                >
                    End Game → Final
                </button>
            </div>
        </div>
    );
}

export default function GamesTab() {
    const [teams, setTeams] = useState([]);
    const [games, setGames] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [potgPlayers, setPotgPlayers] = useState([]);
    const [broadcastGame, setBroadcastGame] = useState(null);
    const [scanGame, setScanGame] = useState(null);
    const [autoOpen, setAutoOpen] = useState(false);
    const [autoForm, setAutoForm] = useState({ start_date: "", interval_days: 7, replace: false });
    const [autoSaving, setAutoSaving] = useState(false);

    const openAuto = () => {
        const d = new Date(Date.now() + 7 * 86400000);
        setAutoForm({ start_date: d.toISOString().slice(0, 10), interval_days: 7, replace: false });
        setAutoOpen(true);
    };

    const runAuto = async (e) => {
        e.preventDefault();
        setAutoSaving(true);
        try {
            const payload = {
                start_date: new Date(`${autoForm.start_date}T00:00:00`).toISOString(),
                interval_days: parseInt(autoForm.interval_days, 10) || 7,
                replace: autoForm.replace,
            };
            const { data } = await api.post("/games/autogenerate", payload);
            toast.success(
                `Created ${data.created} games — ${data.rounds} rounds, ${data.games_per_team} games per team${data.byes_per_team ? `, ${data.byes_per_team} byes each` : ""}`,
            );
            setAutoOpen(false);
            load();
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setAutoSaving(false);
        }
    };

    const load = () => api.get("/games").then((r) => setGames(r.data)).catch(() => {});
    useEffect(() => {
        api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
        load();
    }, []);

    useEffect(() => {
        if (!open) return;
        const ids = [form.home_team_id, form.away_team_id].filter(Boolean);
        if (!ids.length) {
            setPotgPlayers([]);
            return;
        }
        Promise.all(ids.map((id) => api.get(`/teams/${id}`)))
            .then((rs) =>
                setPotgPlayers(
                    rs.flatMap((r) => r.data.players.map((p) => ({ ...p, team_name: r.data.team.name }))),
                ),
            )
            .catch(() => {});
    }, [open, form.home_team_id, form.away_team_id]);

    const openCreate = () => {
        setEditing(null);
        setForm({ ...EMPTY, date: defaultDate() });
        setOpen(true);
    };
    const openEdit = (g) => {
        setEditing(g);
        setForm({
            home_team_id: g.home_team_id,
            away_team_id: g.away_team_id,
            date: toLocalInput(g.date),
            venue: g.venue || "",
            week: g.week != null ? String(g.week) : "",
            status: g.status,
            home_score: g.home_score != null ? String(g.home_score) : "",
            away_score: g.away_score != null ? String(g.away_score) : "",
            stream_url: g.stream_url || "",
            quarter: g.quarter || "Q1",
            clock: g.clock || "",
            player_of_the_game_id: g.player_of_the_game_id || "",
            potg_statline: g.potg_statline || "",
            featured_home_player_id: g.featured_home_player_id || "",
            featured_away_player_id: g.featured_away_player_id || "",
        });
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const payload = {
            home_team_id: form.home_team_id,
            away_team_id: form.away_team_id,
            date: new Date(form.date).toISOString(),
            venue: form.venue,
            week: form.week === "" ? null : parseInt(form.week, 10),
            status: form.status,
            home_score: form.home_score === "" ? (form.status === "scheduled" ? null : 0) : parseInt(form.home_score, 10),
            away_score: form.away_score === "" ? (form.status === "scheduled" ? null : 0) : parseInt(form.away_score, 10),
            stream_url: form.stream_url,
            quarter: form.status === "scheduled" ? "" : form.quarter,
            clock: form.status === "scheduled" ? "" : form.clock,
            player_of_the_game_id: form.player_of_the_game_id || null,
            potg_statline: form.potg_statline,
        };
        try {
            let gameId = editing?.id;
            if (editing) await api.put(`/games/${editing.id}`, payload);
            else gameId = (await api.post("/games", payload)).data.id;
            await api.put(`/games/${gameId}/featured`, {
                home_player_id: form.featured_home_player_id || null,
                away_player_id: form.featured_away_player_id || null,
            });
            toast.success(editing ? "Game updated" : "Fixture created");
            setOpen(false);
            load();
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (g) => {
        if (!window.confirm("Delete this game?")) return;
        try {
            await api.delete(`/games/${g.id}`);
            toast.success("Game deleted");
            load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    const teamSelect = (key, label) => (
        <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">{label}</Label>
            <Select value={form[key]} onValueChange={(v) => setForm({ ...form, [key]: v })}>
                <SelectTrigger data-testid={`game-form-${key}`} className="mt-2 bg-[#08080C] border-[#262638] text-white">
                    <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent className="bg-[#12121A] border-[#262638] text-white">
                    {teams.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                            {t.name}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );

    return (
        <div data-testid="admin-games-tab">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <h2 className="font-display font-bold uppercase text-xl text-white">Fixtures ({games.length})</h2>
                <div className="flex gap-2">
                    <button
                        data-testid="auto-schedule-button"
                        onClick={openAuto}
                        disabled={teams.length < 2}
                        className="flex items-center gap-2 border border-[#FFB800]/60 text-[#FFB800] hover:bg-[#FFB800] hover:text-black disabled:opacity-50 font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-full transition-colors duration-200"
                    >
                        <CalendarClock className="w-4 h-4" /> Auto-Schedule
                    </button>
                    <button
                        data-testid="add-game-button"
                        onClick={openCreate}
                        disabled={teams.length < 2}
                        className="flex items-center gap-2 bg-[#FF5500] hover:bg-[#E04800] disabled:opacity-50 text-white font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-full transition-colors duration-200"
                    >
                        <Plus className="w-4 h-4" /> New Fixture
                    </button>
                </div>
            </div>

            <div className="space-y-3">
                {games.map((g) => (
                    <div
                        key={g.id}
                        data-testid={`admin-game-row-${g.id}`}
                        className="card-arena rounded-2xl px-5 py-4 flex flex-wrap items-center gap-4"
                    >
                        <div className="flex items-center gap-3 flex-1 min-w-[220px]">
                            <TeamCrest team={g.home_team} size="sm" />
                            {g.status !== "scheduled" ? (
                                <span className="font-stat font-bold text-lg">
                                    <span className={g.home_score > g.away_score ? "text-[#FF5500]" : "text-slate-400"}>{g.home_score}</span>
                                    <span className="text-slate-600 mx-1">:</span>
                                    <span className={g.away_score > g.home_score ? "text-[#FF5500]" : "text-slate-400"}>{g.away_score}</span>
                                </span>
                            ) : (
                                <span className="font-display font-black text-lg text-stroke-faint">VS</span>
                            )}
                            <TeamCrest team={g.away_team} size="sm" />
                            <span className="text-xs text-slate-500 font-display uppercase tracking-wider">
                                {g.home_team?.short_name} vs {g.away_team?.short_name}
                            </span>
                        </div>
                        <div className="text-xs text-slate-400 font-stat">
                            {format(new Date(g.date), "MMM d, h:mm a")}
                            {g.week ? ` • W${g.week}` : ""}
                        </div>
                        <span
                            className={`flex items-center gap-1.5 text-[10px] font-display font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${
                                g.status === "final"
                                    ? "bg-[#22C55E]/10 text-[#22C55E] border-[#22C55E]/40"
                                    : g.status === "live"
                                      ? "bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/40"
                                      : "bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/40"
                            }`}
                        >
                            {g.status === "live" && <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] animate-pulse" />}
                            {g.status === "final" ? "Final" : g.status === "live" ? `Live ${g.quarter || ""}` : "Scheduled"}
                        </span>
                        <div className="flex gap-1">
                            {g.status === "live" && (
                                <button
                                    data-testid={`broadcast-game-${g.id}`}
                                    onClick={() => setBroadcastGame(g)}
                                    className="flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-widest px-3 py-2 rounded-full bg-[#EF4444]/15 border border-[#EF4444]/50 text-[#EF4444] hover:bg-[#EF4444] hover:text-white transition-colors duration-200"
                                >
                                    <Video className="w-3.5 h-3.5" /> Broadcast
                                </button>
                            )}
                            <button
                                data-testid={`edit-game-${g.id}`}
                                onClick={() => openEdit(g)}
                                className="flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-widest px-3 py-2 rounded-full border border-[#262638] text-slate-300 hover:border-[#FFB800] hover:text-[#FFB800] transition-colors duration-200"
                            >
                                <Pencil className="w-3.5 h-3.5" /> {g.status === "final" ? "Edit" : "Result"}
                            </button>
                            <a
                                data-testid={`scoresheet-print-${g.id}`}
                                href={`/scoresheet/${g.id}`}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-widest px-3 py-2 rounded-full border border-[#262638] text-slate-300 hover:border-[#FFB800] hover:text-[#FFB800] transition-colors duration-200"
                            >
                                <Printer className="w-3.5 h-3.5" /> Sheet
                            </a>
                            <button
                                data-testid={`scoresheet-scan-${g.id}`}
                                onClick={() => setScanGame(g)}
                                className="flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-widest px-3 py-2 rounded-full border border-[#262638] text-slate-300 hover:border-[#22C55E] hover:text-[#22C55E] transition-colors duration-200"
                            >
                                <ScanLine className="w-3.5 h-3.5" /> Scan
                            </button>
                            <button
                                data-testid={`delete-game-${g.id}`}
                                onClick={() => remove(g)}
                                className="p-2 text-slate-400 hover:text-[#EF4444] transition-colors duration-200"
                                aria-label="Delete game"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                        {g.status === "live" && <LiveBoard game={g} onSaved={load} />}
                    </div>
                ))}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-[#12121A] border-[#262638] text-white max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black uppercase text-2xl">
                            {editing ? "Edit Game / Enter Result" : "New Fixture"}
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                            Create or edit a fixture, set it live, enter the final result and player of the game.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submit} data-testid="game-form" className="space-y-4 mt-2">
                        <div className="grid grid-cols-2 gap-4">
                            {teamSelect("home_team_id", "Home Team")}
                            {teamSelect("away_team_id", "Away Team")}
                        </div>
                        {(form.home_team_id || form.away_team_id) && (
                            <div className="grid grid-cols-2 gap-4">
                                {["home", "away"].map((side) => {
                                    const teamId = form[`${side}_team_id`];
                                    const roster = potgPlayers.filter((p) => p.team_id === teamId);
                                    if (!teamId || !roster.length)
                                        return <div key={side} />;
                                    const key = `featured_${side}_player_id`;
                                    return (
                                        <div key={side}>
                                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">
                                                Epic Card Star ({side})
                                            </Label>
                                            <Select
                                                value={form[key] || "auto"}
                                                onValueChange={(v) => setForm({ ...form, [key]: v === "auto" ? "" : v })}
                                            >
                                                <SelectTrigger
                                                    data-testid={`game-form-featured-${side}`}
                                                    className="mt-2 bg-[#08080C] border-[#262638] text-white"
                                                >
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="bg-[#12121A] border-[#262638] text-white">
                                                    <SelectItem value="auto">Auto-rotate per game</SelectItem>
                                                    {roster.map((p) => (
                                                        <SelectItem key={p.id} value={p.id}>
                                                            #{p.number} {p.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Tip-off</Label>
                                <Input
                                    required
                                    type="datetime-local"
                                    data-testid="game-form-date"
                                    value={form.date}
                                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                                    className="mt-2 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white [color-scheme:dark]"
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Week</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    data-testid="game-form-week"
                                    value={form.week}
                                    onChange={(e) => setForm({ ...form, week: e.target.value })}
                                    className="mt-2 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Venue</Label>
                            <Select value={form.venue} onValueChange={(v) => setForm({ ...form, venue: v })}>
                                <SelectTrigger
                                    data-testid="game-form-venue"
                                    className="mt-2 bg-[#08080C] border-[#262638] text-white"
                                >
                                    <SelectValue placeholder="Select venue" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#12121A] border-[#262638] text-white">
                                    {VENUES.map((v) => (
                                        <SelectItem key={v} value={v}>
                                            {v}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">
                                Live Stream URL (YouTube / Twitch)
                            </Label>
                            <Input
                                data-testid="game-form-stream-url"
                                value={form.stream_url}
                                onChange={(e) => setForm({ ...form, stream_url: e.target.value })}
                                className="mt-2 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white"
                                placeholder="https://youtube.com/watch?v=…"
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Status</Label>
                            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                <SelectTrigger data-testid="game-form-status" className="mt-2 bg-[#08080C] border-[#262638] text-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#12121A] border-[#262638] text-white">
                                    <SelectItem value="scheduled">Scheduled</SelectItem>
                                    <SelectItem value="live">Live</SelectItem>
                                    <SelectItem value="final">Final</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {form.status !== "scheduled" && (
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Home Score</Label>
                                    <Input
                                        required
                                        type="number"
                                        min="0"
                                        data-testid="game-form-home-score"
                                        value={form.home_score}
                                        onChange={(e) => setForm({ ...form, home_score: e.target.value })}
                                        className="mt-2 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white font-stat"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Away Score</Label>
                                    <Input
                                        required
                                        type="number"
                                        min="0"
                                        data-testid="game-form-away-score"
                                        value={form.away_score}
                                        onChange={(e) => setForm({ ...form, away_score: e.target.value })}
                                        className="mt-2 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white font-stat"
                                    />
                                </div>
                            </div>
                        )}
                        {form.status !== "scheduled" && (
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Quarter</Label>
                                    <Select value={form.quarter} onValueChange={(v) => setForm({ ...form, quarter: v })}>
                                        <SelectTrigger data-testid="game-form-quarter" className="mt-2 bg-[#08080C] border-[#262638] text-white">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#12121A] border-[#262638] text-white">
                                            {QUARTERS.map((q) => (
                                                <SelectItem key={q} value={q}>
                                                    {q}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Game Clock</Label>
                                    <Input
                                        data-testid="game-form-clock"
                                        value={form.clock}
                                        onChange={(e) => setForm({ ...form, clock: e.target.value })}
                                        className="mt-2 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white font-stat"
                                        placeholder="4:32"
                                    />
                                </div>
                            </div>
                        )}
                        {form.status === "final" && (
                            <div className="space-y-4">
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">
                                        Player of the Game
                                    </Label>
                                    <Select
                                        value={form.player_of_the_game_id}
                                        onValueChange={(v) => setForm({ ...form, player_of_the_game_id: v })}
                                    >
                                        <SelectTrigger data-testid="game-form-potg" className="mt-2 bg-[#08080C] border-[#262638] text-white">
                                            <SelectValue placeholder="Select player" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#12121A] border-[#262638] text-white">
                                            {potgPlayers.map((p) => (
                                                <SelectItem key={p.id} value={p.id}>
                                                    #{p.number} {p.name} — {p.team_name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">POTG Stat Line</Label>
                                    <Input
                                        data-testid="game-form-statline"
                                        value={form.potg_statline}
                                        onChange={(e) => setForm({ ...form, potg_statline: e.target.value })}
                                        className="mt-2 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white font-stat"
                                        placeholder="34 PTS • 8 REB • 5 AST"
                                    />
                                </div>
                            </div>
                        )}
                        <button
                            type="submit"
                            data-testid="game-form-submit"
                            disabled={saving || !form.home_team_id || !form.away_team_id}
                            className="w-full flex items-center justify-center gap-2 bg-[#FF5500] hover:bg-[#E04800] disabled:opacity-60 text-white font-display font-bold uppercase tracking-widest py-3 rounded-full transition-colors duration-200"
                        >
                            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                            {editing ? "Save Game" : "Create Fixture"}
                        </button>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={autoOpen} onOpenChange={setAutoOpen}>
                <DialogContent className="bg-[#12121A] border-[#262638] text-white max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black uppercase text-2xl">
                            Auto-Schedule Season
                        </DialogTitle>
                        <DialogDescription className="text-slate-400 text-sm">
                            Double round-robin: every team plays every other team twice — once at home, once away.
                            Games and byes distribute evenly across all teams.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={runAuto} data-testid="auto-schedule-form" className="space-y-4 mt-2">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Season Starts</Label>
                                <Input
                                    required
                                    type="date"
                                    data-testid="auto-schedule-date"
                                    value={autoForm.start_date}
                                    onChange={(e) => setAutoForm({ ...autoForm, start_date: e.target.value })}
                                    className="mt-2 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white [color-scheme:dark]"
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Days Between Rounds</Label>
                                <Input
                                    required
                                    type="number"
                                    min="1"
                                    max="30"
                                    data-testid="auto-schedule-interval"
                                    value={autoForm.interval_days}
                                    onChange={(e) => setAutoForm({ ...autoForm, interval_days: e.target.value })}
                                    className="mt-2 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white"
                                />
                            </div>
                        </div>
                        <label className="flex items-center gap-3 text-sm text-slate-300 cursor-pointer">
                            <input
                                type="checkbox"
                                data-testid="auto-schedule-replace"
                                checked={autoForm.replace}
                                onChange={(e) => setAutoForm({ ...autoForm, replace: e.target.checked })}
                                className="w-4 h-4 accent-[#FF5500]"
                            />
                            Replace existing scheduled (not yet played) games
                        </label>
                        <button
                            type="submit"
                            data-testid="auto-schedule-submit"
                            disabled={autoSaving}
                            className="w-full flex items-center justify-center gap-2 bg-[#FF5500] hover:bg-[#E04800] disabled:opacity-60 text-white font-display font-bold uppercase tracking-widest py-3 rounded-full transition-colors duration-200"
                        >
                            {autoSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                            Generate {teams.length}-Team Schedule
                        </button>
                    </form>
                </DialogContent>
            </Dialog>

            {scanGame && <ScanSheetDialog game={scanGame} onClose={() => setScanGame(null)} onApplied={load} />}

            <Dialog open={!!broadcastGame} onOpenChange={(o) => !o && setBroadcastGame(null)}>
                <DialogContent className="bg-[#12121A] border-[#262638] text-white max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black uppercase text-2xl">
                            Courtside Broadcast
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                            Broadcast this game live from your phone camera to everyone watching the Live page.
                        </DialogDescription>
                    </DialogHeader>
                    {broadcastGame && <BroadcastPanel game={broadcastGame} />}
                </DialogContent>
            </Dialog>
        </div>
    );
}
